import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { newtonsCradle } from "ldrs";
import { Card } from "../../../components/layouts/Cards";
import { Badge } from "../../../components/text/Badge";
import { formatTitle } from "../../../utility/formatText";
newtonsCradle.register();

const ResponsesPanel = ({
  isVisible,
  selectedCategory,
  selectedResponses,
  currentResponseIndex,
  onPrevResponse,
  onNextResponse,
  onRefresh,
  isLoading,
  questionTitle,
}) => {
  if (!isVisible) return null;

  return (
    <div className="h-full flex items-center">
      <div className="p-6 mt-10 w-full">
        <Card className="!shadow-none">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg">Selected Responses</h2>
            {selectedCategory && selectedResponses.length > 0 && (
              // Refresh button
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onRefresh}
                className="text-morange-700 hover:text-morange-800"
                title="Get a new set of responses"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
              </motion.button>
            )}
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <l-newtons-cradle
                size="100"
                speed="1.4"
                color="black"
              ></l-newtons-cradle>
            </div>
          ) : !selectedCategory ? (
            <div className="text-slate-500 italic">
              Click a category in the treemap to see responses
            </div>
          ) : selectedResponses.length === 0 ? (
            <div className="text-slate-500 italic">No responses available</div>
          ) : (
            <div className="flex flex-col gap-4">
              {questionTitle && (
                <p className="text-slate-600 mb-4 text-sm italic">
                  {questionTitle}
                </p>
              )}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-morange-700 font-semibold">
                  {selectedCategory}
                </h3>
                <div className="text-sm text-slate-500">
                  {currentResponseIndex + 1} of {selectedResponses.length}
                </div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentResponseIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white rounded-lg p-4 shadow-sm min-h-[120px] relative"
                >
                  <p className="text-slate-700">
                    {selectedResponses[currentResponseIndex]?.response}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedResponses[currentResponseIndex]?.tags.map(
                      (tag) => (
                        <Badge key={tag} color="msky" isButton={false}>
                          {formatTitle(tag)}
                        </Badge>
                      )
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedResponses[currentResponseIndex]?.demographics.map(
                      (demo) => (
                        <Badge key={demo} color="morange" isButton={false}>
                          {formatTitle(demo)}
                        </Badge>
                      )
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="flex justify-between mt-1">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onPrevResponse}
                  className="px-4 py-2 text-morange-700 hover:underline"
                >
                  ← Previous
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onNextResponse}
                  className="px-4 py-2 text-morange-700 hover:underline"
                >
                  Next →
                </motion.button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResponsesPanel;
